// lib/api/createApiClients.ts
import axios from 'axios';
import {
  Configuration as MainAPIConfiguration,
  AddressApi,
  AnimalSpeciesBreedApi,
  AuthorisedRepresentativeApi,
  CustomFieldApi,
  CustomFormApi,
  DocumentApi,
  EnterpriseActionApi,
  EnterpriseApi,
  EnterprisePrefixApi,
  EntityApi,
  EntityDetailApi,
  EntityDetailCategoryApi,
  EntityDetailConfigApi,
  EntityDetailValueApi,
  FeeApi,
  FormApi,
  MembershipApi,
  MembershipBenefitApi,
  MembershipTypeApi,
  MembershipTypeBenefitAsscApi,
  NoteApi,
  OrganisationApi,
  OrganisationSettingApi,
  RoleUserOrgAssociationApi,
  ScreenApi,
  TimelineApi,
  UserConfigApi,
  WorkOrderApi,
  FormActionAssociationApi,
  BillableEventApi,
  EntityTransferApi,
  ImportBatchApi,
  EntityDetailEnhancerApi,
  ValidationApi as ValidationRuleApi,
  RuleConfigurationApi,
} from '@/apiClient';

import {
  Configuration as AuthAPIConfiguration,
  UserApi,
  UserProfileApi,
  ValidationApi,
} from '@/authApiClient';

import { getCurrentCorrelationId } from '@/lib/correlationId';
import { setupAxiosInterceptors } from '@/services/tokenRefresh';

interface CreateApiClientsOptions {
  token: string;
  refreshToken?: string;
  selectedOrganisationId?: number;
  onTokenRefresh?: (token: string, refreshToken: string | undefined) => void;
}

export const createApiClients = (options: CreateApiClientsOptions | string) => {
  // Support backward compatibility
  const { refreshToken, token, onTokenRefresh } =
    typeof options === 'string'
      ? { token: options, refreshToken: undefined, onTokenRefresh: undefined }
      : options;

  // Get stored organization ID from localStorage
  const selectedOrganisationId = localStorage.getItem('selectedOrganisationId');

  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': getCurrentCorrelationId() || '',
    'X-Request-ID': getCurrentCorrelationId() || '',
    ...(selectedOrganisationId && {
      'X-Organisation-Id': selectedOrganisationId,
    }),
  };

  // Create axios instances
  const mainAxiosInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL,
    headers,
  });

  const authAxiosInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
    headers: {
      ...headers,
      'ABRI-AUTH-CLIENT-ID': process.env.NEXT_PUBLIC_ABRI_AUTH_CLIENT_ID,
    },
  });

  const elsaAxiosInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_ELSA_API_URL,
    timeout: 5000,
    headers,
  });

  const getTokens = () => ({ token, refreshToken });
  const safeOnTokenRefresh = onTokenRefresh ?? (() => {});
  setupAxiosInterceptors(mainAxiosInstance, getTokens, safeOnTokenRefresh);
  setupAxiosInterceptors(authAxiosInstance, getTokens, safeOnTokenRefresh);
  setupAxiosInterceptors(elsaAxiosInstance, getTokens, safeOnTokenRefresh);

  const apiConfig = new MainAPIConfiguration({
    basePath: process.env.NEXT_PUBLIC_API_URL,
    baseOptions: { headers },
  });

  const authConfig = new AuthAPIConfiguration({
    basePath: process.env.NEXT_PUBLIC_AUTH_API_URL,
    baseOptions: {
      headers: {
        ...headers,
        'ABRI-AUTH-CLIENT-ID': process.env.NEXT_PUBLIC_ABRI_AUTH_CLIENT_ID,
      },
    },
  });

  return {
    axiosInstance: mainAxiosInstance,
    elsaApi: elsaAxiosInstance,
    // Main API clients
    addressApi: new AddressApi(apiConfig, undefined, mainAxiosInstance),
    animalSpeciesBreedApi: new AnimalSpeciesBreedApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    authorisedRepresentativeApi: new AuthorisedRepresentativeApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    billableEventApi: new BillableEventApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    customFieldApi: new CustomFieldApi(apiConfig, undefined, mainAxiosInstance),
    customFormApi: new CustomFormApi(apiConfig, undefined, mainAxiosInstance),
    documentApi: new DocumentApi(apiConfig, undefined, mainAxiosInstance),
    enterpriseActionApi: new EnterpriseActionApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    enterpriseApi: new EnterpriseApi(apiConfig, undefined, mainAxiosInstance),
    validationRuleApi: new ValidationRuleApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    enterprisePrefixApi: new EnterprisePrefixApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityApi: new EntityApi(apiConfig, undefined, mainAxiosInstance),
    entityDetailApi: new EntityDetailApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailCategoryApi: new EntityDetailCategoryApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailConfigApi: new EntityDetailConfigApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailEnhancerApi: new EntityDetailEnhancerApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailValueApi: new EntityDetailValueApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityTransferApi: new EntityTransferApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    feeApi: new FeeApi(apiConfig, undefined, mainAxiosInstance),
    formApi: new FormApi(apiConfig, undefined, mainAxiosInstance),
    formActionAssociationApi: new FormActionAssociationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    importBatchApi: new ImportBatchApi(apiConfig, undefined, mainAxiosInstance),
    membershipApi: new MembershipApi(apiConfig, undefined, mainAxiosInstance),
    membershipBenefitApi: new MembershipBenefitApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    membershipTypeApi: new MembershipTypeApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    membershipTypeBenefitAsscApi: new MembershipTypeBenefitAsscApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    noteApi: new NoteApi(apiConfig, undefined, mainAxiosInstance),
    organisationApi: new OrganisationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    organisationSettingApi: new OrganisationSettingApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    roleUserOrgAssociationApi: new RoleUserOrgAssociationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    ruleConfigurationApi: new RuleConfigurationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    screenApi: new ScreenApi(apiConfig, undefined, mainAxiosInstance),
    timelineApi: new TimelineApi(apiConfig, undefined, mainAxiosInstance),
    userConfigApi: new UserConfigApi(apiConfig, undefined, mainAxiosInstance),
    workOrderApi: new WorkOrderApi(apiConfig, undefined, mainAxiosInstance),
    // Auth API clients
    userApi: new UserApi(authConfig, undefined, authAxiosInstance),
    userProfileApi: new UserProfileApi(
      authConfig,
      undefined,
      authAxiosInstance,
    ),
    validationApi: new ValidationApi(authConfig, undefined, authAxiosInstance),
  };
};
